import { computed } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import { IssueSeverity, IssueType } from '@/enums/issue'
import { formatDateTime, fromNow } from '@/libs/dayjs'
import { t, te } from '@/libs/i18n'
import type { SelectOption, TagItem } from '@/types/Common'
import type { IIssue } from '@/types/Issue'
import { formatBytes, formatDurationSeconds, formatPercent, shortId } from '@/utils/format'

/**
 * The issue renderer of §9.5 — **one** renderer for all sixteen types, because the server sends
 * `type` + `params{}` and never prose.
 *
 * # The whole rule, in one line
 *
 * `t('issues.' + type, params)`. A Go-side English sentence cannot be localized into sl, so the
 * server is not allowed to compose one; what it sends instead is a key and its substitution values,
 * and this file is the only place that turns the pair into words. That is also why there is no
 * `switch` over sixteen types here producing sixteen different layouts: a seventeenth type would
 * then need a UI change to be visible at all, when what it actually needs is two locale entries.
 *
 * # Params are normalized before substitution, and that is not a violation of the rule
 *
 * The server sends `available_bytes: 1503238553`, not `"1.4 GiB"` — the unit choice is a
 * presentation decision, and a Go-side one would be untranslatable in exactly the way §9.5
 * forbids. So numbers arrive raw and are formatted here, by the same helpers the meters and the
 * tables use, so `12.4 GiB` means the same thing on every screen in the panel.
 *
 * # Third-party output is never translated and never interpolated
 *
 * Traefik's reload errors and Docker's words travel as `raw_*` and are shown **verbatim in a
 * `<pre>`** (§9.5, D58): they are the one thing on the screen that must not be reworded, wrapped or
 * summarized, because an operator is going to paste them into a search box. They are therefore
 * stripped out of the interpolation map and surfaced separately by [issueRawText].
 *
 * # Colours are semantic, never named
 *
 * `critical` → `error`, `warning` → `warning` in Nuxt UI's vocabulary. The two severity words never
 * appear in a locale file as themselves and never pick a hex.
 */

// ---------------------------------------------------------------------------------------------
// Poll rates and deep-link parameters
// ---------------------------------------------------------------------------------------------

/** §9.2's table: the D59 issues page polls `GET /issues` every 30 s. */
export const IssuesPollMs = 30_000

/**
 * The query parameters `/issues` seeds its filters from, and therefore the contract every deep link
 * into that page is written against — the dashboard tiles, and any future link from anywhere else.
 *
 * They are spelled exactly as the server's own filter fields (§6.1: `?severity=`, `?type=`,
 * `?instance_id=`), because the page turns each one into `<field>.eq=<value>` on the wire. One
 * spelling for the URL an operator can bookmark and for the query it produces is one fewer mapping
 * table to keep in step.
 */
export const IssueFilterParam = {
    Severity: 'severity',
    Type:     'type',
    Instance: 'instance_id',
} as const

/** The `/issues` route, pre-filtered — what every dashboard tile that counts issues links to. */
export const issuesFilteredRoute = (filters: Partial<Record<string, string>>): RouteLocationRaw => {
    const query: Record<string, string> = {}

    for (const [key, value] of Object.entries(filters)) {
        if (value) query[key] = value
    }

    return { name: 'issues', query }
}

// ---------------------------------------------------------------------------------------------
// Severity (§9.5) — semantic colours only
// ---------------------------------------------------------------------------------------------

/**
 * Severity as a chip. `critical` is `error` and `warning` is `warning`, which is the whole of the
 * map — and the reason the two stored words never reach a locale file as themselves: what an
 * operator reads is *"Critical"* / *"Kritično"* from `issues.severity.*`, and what they see is a
 * colour Nuxt UI resolves from the theme.
 */
export const getIssueSeverity = (severity: string): TagItem => {
    const map: Record<string, TagItem> = {
        [IssueSeverity.Critical]: { label: t('issues.severity.critical'), color: 'error' },
        [IssueSeverity.Warning]:  { label: t('issues.severity.warning'),  color: 'warning' },
    }

    return map[severity] ?? { label: severity, color: 'neutral' }
}

/** The same decision as a bare colour, for the places that tint an icon rather than draw a chip. */
export const issueSeverityColor = (severity: string): 'error' | 'warning' | 'neutral' => {
    if (severity === IssueSeverity.Critical) return 'error'
    if (severity === IssueSeverity.Warning)  return 'warning'

    return 'neutral'
}

// ---------------------------------------------------------------------------------------------
// Type — label, icon, and the filter's option list
// ---------------------------------------------------------------------------------------------

/**
 * The short name of a type — the filter's option label and the row's own caption.
 *
 * It falls back to the raw value rather than to a key path: a panel one release behind its fleet
 * receives a type it has no translation for, and *"host_cpu_v2"* on a row is legible while
 * *"issues.host_cpu_v2"* is a bug report from an operator during an incident.
 */
export const issueTypeLabel = (type: string): string => {
    const key = `issues.types.${type}`

    return te(key) ? t(key) : type
}

/**
 * One icon per type. It is a glyph for the *subject* — a disk, a certificate, a clock — and never a
 * severity: the colour beside it already says how bad this is, and an icon that also said so would
 * be a second, redundant channel that stops working the moment a type escalates.
 *
 * Names are literals on purpose: `clientBundle.scan` in `vite.config.ts` greps the sources for
 * `i-lucide-*` strings, and an icon name assembled at runtime would not be bundled.
 */
export const issueIcon = (type: string): string => {
    const map: Record<string, string> = {
        [IssueType.HostCPU]:                   'i-lucide-cpu',
        [IssueType.HostMemory]:                'i-lucide-memory-stick',
        [IssueType.HostDisk]:                  'i-lucide-hard-drive',
        [IssueType.CertExpiring]:              'i-lucide-shield-alert',
        [IssueType.CertExpired]:               'i-lucide-shield-x',
        [IssueType.InstanceOffline]:           'i-lucide-wifi-off',
        [IssueType.StaleAppliedVersion]:       'i-lucide-clock-alert',
        [IssueType.TagMismatch]:               'i-lucide-tag',
        [IssueType.OutdatedAgent]:             'i-lucide-package',
        [IssueType.ReloadFailure]:             'i-lucide-refresh-cw-off',
        [IssueType.RouterServiceErrors]:       'i-lucide-route',
        [IssueType.ClockDrift]:                'i-lucide-clock',
        [IssueType.ConfigPendingFiles]:        'i-lucide-file-clock',
        [IssueType.StaticConfigDrift]:         'i-lucide-file-diff',
        [IssueType.AgentOutboxOverflow]:       'i-lucide-inbox',
        [IssueType.FlattenInvariantViolation]: 'i-lucide-layers',
    }

    return map[type] ?? 'i-lucide-triangle-alert'
}

/**
 * All sixteen, alphabetized **by their translated label** rather than by the wire value.
 *
 * The wire order is W11.2's enumeration, which groups by family and is the right order for a
 * specification; a `USelect` of sixteen entries is scanned by eye, so it is sorted the way the
 * reader sees it — and re-sorted when the locale changes, which is why this is a `computed`.
 */
export const issueTypeOptions = computed((): SelectOption[] =>
    Object.values(IssueType)
        .map(type => ({ label: issueTypeLabel(type), value: type as string }))
        .sort((first, second) => first.label.localeCompare(second.label)))

export const issueSeverityOptions = computed((): SelectOption[] => [
    { label: t('issues.severity.critical'), value: IssueSeverity.Critical },
    { label: t('issues.severity.warning'),  value: IssueSeverity.Warning },
])

// ---------------------------------------------------------------------------------------------
// The message (§9.5) — `t('issues.' + type, params)`
// ---------------------------------------------------------------------------------------------

/** How many entries of a list param reach the sentence before it is cut with an ellipsis. */
const ListParamSample = 3

/**
 * `raw_*` — third-party output. Never interpolated, never translated, shown verbatim in a `<pre>`.
 */
const RawParamPrefix = 'raw_'

/**
 * One param value, formatted by what its **name** says it is.
 *
 * The suffix convention is the server's own (`*_bytes`, `*_percent`, `*_seconds`, `*_at`) and it is
 * consistent across all sixteen rule sets in `Logic/IssueRules.go`, so a new param of a known kind
 * formats correctly the day it is added — without an edit here, and without a sixteen-armed switch
 * that would have to name every param the panel has ever sent.
 *
 * Anything unrecognized is returned as-is: a number is a number, and a string the server chose is
 * already the thing to show.
 */
const formatParam = (key: string, value: unknown): unknown => {
    if (value === null || value === undefined) return '—'

    if (Array.isArray(value)) {
        const sample = value.slice(0, ListParamSample).map(item => String(item)).join(', ')

        return value.length > ListParamSample ? `${sample} …` : sample
    }

    if (typeof value === 'number') {
        if (key.endsWith('_percent')) return formatPercent(value)
        if (key.endsWith('_bytes'))   return formatBytes(value)
        if (key.endsWith('_seconds')) return formatDurationSeconds(value)

        // `days_left` and `threshold_days` carry the SI symbol for the same reason
        // `formatDurationSeconds` does: *"14 d"* is one string in both locales, while *"14 days"*
        // needs two plural forms in English and four in Slovenian for a number that is only ever
        // read as a magnitude.
        if (key.endsWith('_days') || key === 'days_left') return `${Math.round(value)} d`
    }

    // A digest is 64 characters and is only ever compared by eye. Twelve is what `docker ps` shows
    // and what an operator will paste into a command — the same cut `shortId` makes everywhere else.
    if (typeof value === 'string' && key.endsWith('_sha256')) return shortId(value)

    // `*_at` and `not_after` are instants. They are rendered in the operator's own timezone with
    // the panel's one date format, so an expiry inside a sentence and the same expiry in the
    // certificates table are the same string.
    if (typeof value === 'string' && (key.endsWith('_at') || key === 'not_after')) {
        return formatDateTime(value) || value
    }

    return value
}

/**
 * The interpolation map for one issue: every server param formatted, plus `subject` and `open_for`.
 *
 * `subject` rides along because it is the row's identity — a mount path, a certificate serial — and
 * several messages read better naming it than repeating a param that holds the same value. It is
 * added rather than overwritten: no rule in `Logic/IssueRules.go` sends a param called `subject`,
 * and if one ever did, the server's own value is the one that would be right.
 *
 * # `open_for` is computed here, and the server is *forbidden* from sending it
 *
 * Five of the sixteen rules are duration rules, and the obvious thing for them to send is the
 * elapsed time. It is the one thing they must not: `params` is inside the `/dashboard` and `/issues`
 * ETags, so a value that grows every sweep tick rewrites the served body once a minute for as long
 * as anything is wrong, and M11's acceptance criterion is *"one aggregated request per tab per 15 s,
 * mostly 304s"*. So the server sends only instants that do not move — `last_seen_at`,
 * `delivered_at`, `mismatched_at`, `outdated_at`, `over_threshold_at` — and the elapsed time is
 * derived from `since`, here, where it has to be re-derived on every render anyway because the clock
 * advances whether or not the document changed.
 *
 * `fromNow(…, true)` rather than a number: Slovenian needs four plural forms and English two, and
 * Day.js's own locales carry them. A server-side "3 hours" would be untranslatable in exactly the
 * way §9.5 forbids.
 */
export const issueParams = (issue: IIssue): Record<string, unknown> => {
    const params: Record<string, unknown> = {
        subject:  issue.subject,
        open_for: fromNow(issue.since, true),
    }

    for (const [key, value] of Object.entries(issue.params ?? {})) {
        if (key.startsWith(RawParamPrefix)) continue

        params[key] = formatParam(key, value)
    }

    return params
}

/**
 * The sentence an operator reads — §9.5's `t('issues.' + type, params)` and nothing else.
 *
 * The `te` guard is the same one `commandErrorLabel` uses and exists for the same reason: a missing
 * key resolves to the key itself in vue-i18n, so without it a panel that has not been redeployed
 * after the server learned a seventeenth type would render `issues.whatever_it_is` into the middle
 * of an incident. The raw type is at least a word somebody can search for.
 */
export const issueMessage = (issue: IIssue): string => {
    const key = `issues.${issue.type}`

    return te(key) ? t(key, issueParams(issue)) : issue.type
}

/**
 * The untranslatable half: Traefik's or Docker's own words, verbatim, or an empty string.
 *
 * Every `raw_*` param is joined rather than only the first: `reload_failure` sends one today, and a
 * rule that ever sends two would otherwise have the second silently dropped. The server bounds
 * their length before they are stored (`boundedRawText`), so nothing here has to.
 */
export const issueRawText = (issue: IIssue): string =>
    Object.entries(issue.params ?? {})
        .filter(([key, value]) => key.startsWith(RawParamPrefix) && typeof value === 'string' && value !== '')
        .map(([, value]) => String(value))
        .join('\n')

// ---------------------------------------------------------------------------------------------
// The deep link (§7.1) — "the exact tab that fixes it"
// ---------------------------------------------------------------------------------------------

/**
 * The issue's own route, pushed verbatim.
 *
 * §7.1 requires that each issue link to the affected instance; M11's deliverable sharpens it to
 * *"the exact tab that fixes it"* — a `static_config_drift` goes to the static-config tab, where
 * the diff and the discard button are, not to the overview where an operator would read that there
 * is drift and then have to go looking.
 *
 * A row whose route the server did not fill (an issue written by an older panel, a document edited
 * by hand) falls back to the instance overview, which is always a real destination. It is never
 * `undefined`: a button that renders and then navigates nowhere is worse than one that lands one
 * click away from the answer.
 */
export const issueRouteOf = (issue: IIssue): RouteLocationRaw => {
    if (issue.route?.name) {
        return { name: issue.route.name, params: issue.route.params ?? {} }
    }

    return { name: 'instance-overview', params: { instanceId: issue.instance_id } }
}
